import { useEffect, useState } from 'react'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { CircularProgress } from '@mui/material'
import useGetMergedExtendedColumns from '../../generic/hooks/useGetExtendedColumns/useGetMergedExtendedColumns'
import { apiGet } from '../../generic/Api_Functions'
import { formatCodeName } from './utils'

/**
 *
 * @param {object} props
 * @param {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} props.workflow
 * @returns
 */
const WorkflowStructure = ({ workflow }) => {
  const { t_ } = useSmartTranslation()
  const extendedColumns = useGetMergedExtendedColumns('job_reference', 'workflow')
  const [data, setData] = useState()

  useEffect(() => {
    setData(undefined)

    /** @param {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} workflow  @param {boolean} isSubWorkflow @param {string} rootWorkflowName @param {string} subWorkflowNodeName */
    const getDataFromNodes = async (workflow, isSubWorkflow, rootWorkflowName, subWorkflowNodeName) => {
      let newData = []

      if (isSubWorkflow) {
        const subWorkflowRecord = {
          workflow: rootWorkflowName,
          subWorkflow: subWorkflowNodeName,
          jobReference: null,
          indexValue: workflow.indexValue?.name
        }
        for (const extendedColumn of extendedColumns) {
          if (extendedColumn.name === 'Version') {
            subWorkflowRecord[extendedColumn.field] = workflow?.version ?? null
          } else {
            subWorkflowRecord[extendedColumn.field] = workflow?.[extendedColumn.field] ?? null
          }
        }
        newData.push(subWorkflowRecord)
      }

      for (const node of workflow.nodes ?? []) {
        if (node.nodeType === 'JOB') {
          const newRecord = {}
          newRecord.workflow = rootWorkflowName
          newRecord.subWorkflow = isSubWorkflow ? subWorkflowNodeName : null
          // S34 (RN10.2/RN01.4): identify the job node by its own code+name, never node.name alone.
          newRecord.jobReference = formatCodeName(node.code, node.name)
          newRecord.indexValue = node.jobReference?.indexValue?.name
          for (const extendedColumn of extendedColumns) {
            newRecord[extendedColumn.field] = node.jobReference?.[extendedColumn.field] ?? null
          }
          newData.push(newRecord)
          continue
        }

        let subWorkflow = node.subWorkflow
        if (!subWorkflow.nodes && subWorkflow.id) {
          subWorkflow = await apiGet('workflow/' + subWorkflow.id)
        }
        newData = newData.concat(await getDataFromNodes(subWorkflow, true, rootWorkflowName, formatCodeName(node.code, node.name)))
      }
      return newData
    }

    if (extendedColumns === undefined || !workflow) return

    const getData = async () => {
      // S34 (RN10.2/RN01.4): identify the root workflow by its own code+name, never workflow.name
      // alone (now the free descriptive field, often empty).
      const rootRecord = {
        workflow: formatCodeName(workflow.code, workflow.name),
        subWorkflow: null,
        jobReference: null,
        indexValue: workflow.indexValue?.name
      }
      for (const extendedColumn of extendedColumns) {
        if (extendedColumn.name === 'Version') {
          rootRecord[extendedColumn.field] = workflow?.version ?? null
        } else {
          rootRecord[extendedColumn.field] = workflow?.[extendedColumn.field] ?? null
        }
      }
      await getDataFromNodes(workflow, false, formatCodeName(workflow.code, workflow.name), null).then((newData) => {
        setData([rootRecord, ...newData])
      })
    }

    getData().catch((error) => {
      console.error('Error loading workflow structure:', error)
      setData([])
    })
  }, [workflow, extendedColumns])

  const columns = [
    {
      name: t_('Workflow'),
      field: 'workflow'
    },
    {
      name: t_('SubWorkflow'),
      field: 'subWorkflow'
    },
    {
      name: t_('Job Reference'),
      field: 'jobReference'
    },
    {
      name: t_('Index'),
      field: 'indexValue'
    }
  ]

  const getColumns = () => {
    if (!extendedColumns || extendedColumns?.length === 0) {
      return columns
    }
    return columns.concat(extendedColumns)
  }

  if (extendedColumns === undefined || !extendedColumns || !data) {
    return <CircularProgress />
  }

  return (
    <SmartMateriaUITable
      tableTuneUniqueKey='workflowStructure'
      columns={getColumns()}
      title={t_('Workflow structure')}
      dataFetch={data}
    />
  )
}

export default WorkflowStructure
